import React, { useCallback, useState, useEffect, useRef, createContext, useContext, useMemo } from 'react';
import './App.css';
import AuthScreen from './components/AuthScreen';
import ResetPasswordScreen from './components/ResetPasswordScreen';
import ProfileSetup from './components/ProfileSetup';
import Dashboard from './components/Dashboard';
import FoodTracker from './components/FoodTracker';
import NutritionHub from './components/NutritionHub';
import ExerciseTracker from './components/ExerciseTracker';
import AdminDashboard from './components/AdminDashboard';
import MessagesView from './components/MessagesView';
import GoalsView from './components/GoalsView';
import FavoritesView from './components/FavoritesView';
import HistoryView from './components/HistoryView';
import ProfileView from './components/ProfileView';
import WearablesView from './components/WearablesView';
import CoachView from './components/CoachView';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import WeighInModal from './components/WeighInModal';
import WeightHistoryView from './components/WeightHistoryView';
import CalendarView from './components/CalendarView';
import HabitsView from './components/HabitsView';
import TrainerDashboard from './components/TrainerDashboard';
import GoalModal from "./components/GoalModal";
import MobileBottomNav from "./components/MobileBottomNav";
import HeaderTabs from "./components/HeaderTabs";
import WorkoutSessionView from "./components/WorkoutSessionView";
import WorkoutsHub from "./components/WorkoutsHub";
import LogChoiceModal from './components/LogChoiceModal';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';

import { BACKEND_URL } from './config';

// ---- URL <-> view helpers (SPA routing without react-router) ----
const pathToView = (path) => {
  switch (path) {
    case "/privacy":
      return "privacy";
    case "/terms":
      return "terms";
    case "/":
    default:
      return "dashboard";
  }
};

const viewToPath = (view) => {
  switch (view) {
    case "privacy":
      return "/privacy";
    case "terms":
      return "/terms";
    default:
      return "/";
  }
};

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function App() {
  const [authUser, setAuthUser] = useState(null);
  const [appZoom, setAppZoom] = useState(() => {
    try {
      const saved = Number(localStorage.getItem('ht_app_zoom'));
      return Number.isFinite(saved) && saved >= 0.5 && saved <= 1.2 ? saved : 1;
    } catch {
      return 1;
    }
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [resetToken, setResetToken] = useState(null);
  const getInitialAppState = () => {
    try {
      const saved = JSON.parse(sessionStorage.getItem("healthtrail_nav_state") || "{}");
      const pathView = pathToView(window.location.pathname);

      return {
        currentView: saved.currentView || pathView || "dashboard",
        favoritesType: saved.favoritesType || "food",
        historyType: saved.historyType || "overview",
      };
    } catch {
      return {
        currentView: pathToView(window.location.pathname),
        favoritesType: "food",
        historyType: "overview",
      };
    }
  };

  const initialAppState = getInitialAppState();
  const [currentView, setCurrentView] = useState(initialAppState.currentView);  
  const [toast, setToast] = useState({ open: false, message: "", type: "info" });

  const showToast = useCallback((message, type = "success") => {
    setToast({ open: true, message, type });
    window.clearTimeout(window.__appToastTimer);
    window.__appToastTimer = window.setTimeout(() => {
      setToast((prev) => ({ ...prev, open: false }));
    }, 2800);
  }, []);
  
  const hideToast = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    const clamped = Math.max(0.5, Math.min(1.2, Number(appZoom) || 1));
    document.documentElement.style.setProperty('--app-zoom', String(clamped));
    try {
      localStorage.setItem('ht_app_zoom', String(clamped));
    } catch {}
  }, [appZoom]);

  useEffect(() => {
    // Check for reset token in URL
    const params = new URLSearchParams(window.location.search);
    const resetTokenParam = params.get('reset_token');
    if (resetTokenParam) {
      setResetToken(resetTokenParam);
      setLoading(false);
      return;
    }

    // Check if we have a stored token and validate it
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      checkAuth(storedToken);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
    const persistNavState = useCallback((next) => {
    try {
      sessionStorage.setItem(
        "healthtrail_nav_state",
        JSON.stringify({
          currentView: next.currentView,
          favoritesType: next.favoritesType ?? "food",
          historyType: next.historyType ?? "overview",
        })
      );
    } catch {}
  }, []);

    const navigateApp = useCallback((nextView, opts = {}) => {
    const nextState = {
      currentView: nextView,
      favoritesType: opts.favoritesType ?? "food",
      historyType: opts.historyType ?? "overview",
    };

    persistNavState(nextState);
    setCurrentView(nextView);

    const targetPath = viewToPath(nextView);
    const currentState = window.history.state || {};

    if (
      currentState.appView !== nextView ||
      currentState.favoritesType !== nextState.favoritesType ||
      currentState.historyType !== nextState.historyType ||
      window.location.pathname !== targetPath
    ) {
      window.history.pushState(
        {
          appView: nextView,
          favoritesType: nextState.favoritesType,
          historyType: nextState.historyType,
        },
        "",
        targetPath
      );
    }
  }, [persistNavState]);

  const goToView = useCallback((view, opts = {}) => {
    navigateApp(view, opts);
  }, [navigateApp]);

  // Keep the browser URL in sync with currentView
  useEffect(() => {
  const saved = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("healthtrail_nav_state") || "{}");
    } catch {
      return {};
    }
  })();

  persistNavState({
    currentView,
    favoritesType: saved.favoritesType || "food",
    historyType: saved.historyType || "overview",
  });

  const currentState = window.history.state || {};
  if (currentState.appView !== currentView) {
    window.history.replaceState(
      {
        ...currentState,
        appView: currentView,
        favoritesType: saved.favoritesType || "food",
        historyType: saved.historyType || "overview",
      },
      "",
      viewToPath(currentView)
    );
  }
}, [currentView, persistNavState]);

  // Handle browser back/forward
useEffect(() => {
  const onPopState = (event) => {
    const state = event.state || {};
    const nextView = state.appView || pathToView(window.location.pathname);
    const nextFavoritesType = state.favoritesType || "food";
    const nextHistoryType = state.historyType || "overview";

    sessionStorage.setItem(
      "healthtrail_nav_state",
      JSON.stringify({
        currentView: nextView,
        favoritesType: nextFavoritesType,
        historyType: nextHistoryType,
      })
    );

    setCurrentView(nextView);
  };

  window.addEventListener("popstate", onPopState);
  return () => window.removeEventListener("popstate", onPopState);
}, []);

  const checkAuth = async (authToken) => {
    const tokenToUse = authToken || token;
    if (!tokenToUse) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${tokenToUse}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAuthUser(data);
      } else {
        logout();
      }
    } catch (error) {
      logout();
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error('Invalid email or password');
    }

    const data = await response.json();
    setToken(data.access_token);
    setAuthUser(data);
    localStorage.setItem('token', data.access_token);
    return data;
  };

  const register = async (email, password, name) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await response.json();
    setToken(data.access_token);
    setAuthUser(data);
    localStorage.setItem('token', data.access_token);
    return data;
  };

  const logout = () => {
    setToken(null);
    setAuthUser(null);
    localStorage.removeItem('token');
    sessionStorage.removeItem("healthtrail_nav_state");
  };

  const handlePasswordReset = async (newPassword) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const responseText = await response.text();

      let data = {};
      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch {
          data = { detail: responseText };
        }
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Password reset failed');
      }

      showToast('Password reset successfully! Please login with your new password.', 'success');
      setResetToken(null);
      window.history.replaceState({}, '', '/');
      window.location.reload();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw new Error(error.message || 'Password reset failed');
    }
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (resetToken) {
    return <ResetPasswordScreen token={resetToken} onReset={handlePasswordReset} />;
  }

  return (
    <AuthContext.Provider value={{ authUser, token, login, register, logout }}>
      {/* Allow Privacy and Terms pages to be viewed without auth */}
      {(currentView === 'privacy' || currentView === 'terms') ? (
        <div className="app-container" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <header className="app-header">
            <h1 className="logo" onClick={() => navigateApp('dashboard')} style={{ cursor: 'pointer' }}>HealthTrail</h1>
            {!authUser && (
              <button onClick={() => navigateApp('dashboard')} className="btn-secondary" style={{ padding: '8px 16px' }}>
                Back to Login
              </button>
            )}
          </header>
          {currentView === 'privacy' && <PrivacyPolicy />}
          {currentView === 'terms' && <TermsOfService />}
          <footer style={{
            padding: '20px',
            textAlign: 'center',
            borderTop: '1px solid var(--border-light)',
            background: 'var(--bg-secondary)',
            marginTop: 'auto'
          }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
              © 2025 HealthTrail. All rights reserved.
            </p>
          </footer>

          {toast.open && (
            <div className={`app-toast app-toast--${toast.type}`} onClick={hideToast}>
              <div className="app-toast__content">
                <span>{toast.message}</span>
                <button type="button" className="app-toast__close" onClick={hideToast}>
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      ) : !authUser ? (
        <AuthScreen onLogin={login} onRegister={register} onNavigate={goToView} />
      ) : authUser.approved === false ? (
        <PendingApprovalScreen authUser={authUser} onLogout={logout} />
      ) : (
        <MainApp
  currentView={currentView}
  goToView={goToView}
  navigateApp={navigateApp}
  showToast={showToast}
  hideToast={hideToast}
  appZoom={appZoom}
  setAppZoom={setAppZoom}
/>
      )}
    </AuthContext.Provider>
  );
}

function PendingApprovalScreen({ authUser, onLogout }) {
  return (
    <div className="auth-container">
      <div className="auth-card" style={{ textAlign: 'center' }}>
        <h1 className="heading-2" style={{ marginBottom: '16px' }}>Account Pending Approval</h1>
        <div style={{ fontSize: '64px', marginBottom: '24px' }}>⏳</div>
        <p className="body-medium" style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
          Hi <strong>{authUser.name}</strong>, your account is currently pending approval by an administrator.
        </p>
        <p className="body-medium" style={{ marginBottom: '32px', color: 'var(--text-secondary)' }}>
          You'll receive an email at <strong>{authUser.email}</strong> once your account has been approved.
        </p>
        <button onClick={onLogout} className="btn-secondary" style={{ width: '100%' }}>
          Logout
        </button>
      </div>
    </div>
  );
}

function MainApp({ currentView, setCurrentView, navigateApp, showToast, hideToast, appZoom, setAppZoom }) {
  const { authUser, token, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [foodLogs, setFoodLogs] = useState([]);
  const [exerciseLogs, setExerciseLogs] = useState([]);
  const [foodFavorites, setFoodFavorites] = useState([]);
  const [exerciseFavorites, setExerciseFavorites] = useState([]);
  const [history, setHistory] = useState([]);
  const getSavedNavState = () => {
    try {
      return JSON.parse(sessionStorage.getItem("healthtrail_nav_state") || "{}");
    } catch {
      return {};
    }
  };

  const savedNavState = getSavedNavState();

  const [favoritesType, setFavoritesType] = useState(savedNavState.favoritesType || 'food');
  const [historyType, setHistoryType] = useState(savedNavState.historyType || 'overview');
    useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem("healthtrail_nav_state") || "{}");
      if (saved.favoritesType) setFavoritesType(saved.favoritesType);
      if (saved.historyType) setHistoryType(saved.historyType);
    } catch {}
  }, [currentView]);
  const [loading, setLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [analysisState, setAnalysisState] = useState({
    active: false,
    mode: null, // food-photo | exercise-photo | food-voice | exercise-voice
    phase: "analyzing",
    message: "",
  });
  const [predefinedExercises, setPredefinedExercises] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // ✅ NEW: which subnav dropdown is open
  const [openSubNav, setOpenSubNav] = useState(null); // null | "tracking" | "planning"

  const [messages, setMessages] = useState([]);
  const [goals, setGoals] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showWeighInModal, setShowWeighInModal] = useState(false);
  const [analyzedFood, setAnalyzedFood] = useState(null);
  const [showFoodEditModal, setShowFoodEditModal] = useState(false);
  const [details, setDetails] = useState('');
  const exerciseFileInputRef = useRef(null);
  const foodVoiceInputRef = useRef(null);
  const exerciseVoiceInputRef = useRef(null);
  const [openExerciseManual, setOpenExerciseManual] = useState(0);
  const [openExercisePredefined, setOpenExercisePredefined] = useState(0);
  const [openExerciseMachine, setOpenExerciseMachine] = useState(0);
  const [logChoice, setLogChoice] = useState(null); // null | "food" | "exercise"
  const foodFileInputRef = useRef(null);
  const [openFoodManual, setOpenFoodManual] = useState(0);
  const [openFoodQuickAdd, setOpenFoodQuickAdd] = useState(0);  
  const [exerciseCameraMode, setExerciseCameraMode] = useState(false);
  const cameraVideoRef = useRef(null);
  const cameraCanvasRef = useRef(null);
  const streamRef = useRef(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [logChoiceOrigin, setLogChoiceOrigin] = useState(null); // "dashboard" | "food" | "exercise" | null
  const [workoutSessionId, setWorkoutSessionId] = useState(null);
  const [workoutReturnView, setWorkoutReturnView] = useState("dashboard");

  // ✅ NEW: Water logs
  const [waterLogs, setWaterLogs] = useState([]);
  const [goalModalOpen, setGoalModalOpen] = useState(false);
  const [goalModalSource, setGoalModalSource] = useState(null);

  const [toast, setToast] = useState({ open: false, message: "", type: "info" });

  const canManageOthers = authUser?.is_admin || authUser?.is_trainer;
  const people = [];

  const openGoalModal = (source = null) => {
    setGoalModalSource(source);
    setGoalModalOpen(true);
  };
  const closeGoalModal = () => {
    setGoalModalOpen(false);
    setGoalModalSource(null);
  };

  const [mealFeedback, setMealFeedback] = useState([]);

 

  // ✅ Close subnav when clicking outside dropdowns
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!e.target.closest('.nav-dropdown')) {
        setOpenSubNav(null);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  // --- Helper: fetch with auth + auto-logout on 401 ---
  const apiFetch = useCallback(async (path, options = {}) => {
    const url = path.startsWith("http") ? path : `${BACKEND_URL}${path}`;
    const { skipAuth, ...fetchOptions } = options;

    const headers = new Headers(fetchOptions.headers || {});
    if (!skipAuth && token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const res = await fetch(url, { ...fetchOptions, headers });

    if (res.status === 401) {
      logout();
      throw new Error("Unauthorized (401). Please log in again.");
    }

    return res;
  }, [token, logout]);

   const goToView = useCallback((view, opts = {}) => {
    const nextFavoritesType = opts.favoritesType || favoritesType;
    const nextHistoryType = opts.historyType || historyType;

    if (opts.favoritesType) setFavoritesType(opts.favoritesType);
    if (opts.historyType) setHistoryType(opts.historyType);

    navigateApp(view, {
      favoritesType: nextFavoritesType,
      historyType: nextHistoryType,
    });
  }, [navigateApp, favoritesType, historyType]);

  const safeJson = async (res) => {
    const text = await res.text();
    if (!text) return null;
    try { return JSON.parse(text); } catch { return text; }
  };

  useEffect(() => {
    loadProfile();
    loadPredefinedExercises();
    loadUnreadCount();
    checkWeighInNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentView === 'dashboard' && profile) {
      loadDashboardData();
    } else if (currentView === 'food' && profile) {
      loadFoodLogs();
      loadWaterLogs();
    } else if (currentView === 'exercise' && profile) {
      loadExerciseLogs();
    } else if (currentView === 'favorites' && profile) {
      if (favoritesType === 'exercise') {
        loadExerciseFavorites();
      } else {
        loadFoodFavorites();
      }
    } else if (currentView === 'history' && profile) {
      if (historyType !== 'weight') {
        loadHistory();
      }
    } else if (currentView === 'admin' && authUser?.is_admin) {
      loadAdminUsers();
    } else if (currentView === 'messages') {
      loadMessages();
    } else if (currentView === 'goals') {
      loadGoals();
      if (authUser?.is_admin) loadAdminUsers();
    } else if (currentView === 'calendar') {
      if (authUser?.is_admin) loadAdminUsers();
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, profile, historyType, favoritesType]);

  const logChoiceConfig = useMemo(() => {
    if (logChoice === "food") {
      return {
        title: "Choose a Food Logging Method",
        subtitle: "Scan a meal or barcode, upload a food photo, or enter nutrition manually.",
        takePhotoLabel: "📸 Scan Food Photo / Barcode",
        uploadLabel: "⬆️ Upload Food Photo / Barcode",
        voiceLabel: "🎤 Analyze Voice Food Log",
        quickAddLabel: "⚡ Quick Add Food",
        manualLabel: "✍️ Manual Food Entry",
      };
    }

    if (logChoice === "exercise") {
      return {
        title: "Choose an Exercise Logging Method",
        subtitle: "Analyze a workout machine photo, quick add an exercise, or enter exercise details manually.",
        takePhotoLabel: "📷 Analyze Machine Display",
        uploadLabel: "⬆️ Upload Machine Photo",
        voiceLabel: "🎤 Analyze Voice Exercise Log",
        quickAddLabel: "⚡ Quick Add Exercise",
        manualLabel: "✍️ Manual Exercise Entry",
      };
    }

    return null;
  }, [logChoice]);


  const checkWeighInNeeded = async () => {
    if (!authUser?.user_id || !token) return;

    const skipTime = localStorage.getItem('weighInSkipped');
    if (skipTime) {
      const skipDate = new Date(skipTime);
      const hoursSinceSkip = (new Date() - skipDate) / (1000 * 60 * 60);
      if (hoursSinceSkip < 24) return;
    }

    try {
      const logsRes = await apiFetch(`/api/weight/logs/${authUser.user_id}?days=1`);
      let logs = [];

      if (logsRes.ok) {
        const data = await logsRes.json();
        logs = data.logs || data.weight_logs || [];
      }

      const today = new Date().toISOString().slice(0, 10);

      const integrationLoggedToday = logs.some(l => {
        const date = (l.date || "").slice(0, 10);
        if (date !== today) return false;
        return true;
      });

      if (integrationLoggedToday) return;

      const response = await apiFetch(`/api/weight/last-weigh-in/${authUser.user_id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.needs_weigh_in) {
          setTimeout(() => setShowWeighInModal(true), 2000);
        }
      }
    } catch (error) {
      console.error('Error checking weigh-in status:', error);
    }
  };

  const startWorkoutSession = async (eventId, returnView = "dashboard") => {
    if (!token || !eventId) return;
    try {
      const res = await apiFetch(`/api/workouts/sessions/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event_id: eventId }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await safeJson(res);
      const sid = data?.session_id || data?.session?.session_id;
      if (!sid) throw new Error("No session_id returned");
      setWorkoutSessionId(sid);
      setWorkoutReturnView(returnView || "dashboard");
      goToView("workout_session");
    } catch (e) {
      showToast(`Failed to start workout: ${e?.message || String(e)}`, "error");
    }
  };

  const handleWeighInSubmit = (weight) => {
    if (profile) setProfile({ ...profile, weight });
    loadDashboardData();
  };

  const loadProfile = async () => {
    if (!authUser?.user_id) return;
    try {
      const response = await apiFetch(`/api/users/${authUser.user_id}/profile`);
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        const t = (data?.theme || "light").toLowerCase();
        document.documentElement.setAttribute("data-theme", t === "dark" ? "dark" : "light");
      }
    } catch (error) {
      console.log('No profile found / error:', error?.message || error);
    }
  };

  const loadDashboardData = useCallback(async () => {
    if (!authUser?.user_id) return;
    try {
      const response = await apiFetch(`/api/dashboard/${authUser.user_id}`);
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  }, [apiFetch, authUser?.user_id]);

  const loadFoodLogs = async () => {
    if (!authUser?.user_id) return;
    try {
      const response = await apiFetch(`/api/food/logs/${authUser.user_id}`);
      if (response.ok) {
        const data = await response.json();
        setFoodLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error loading food logs:', error);
    }
  };

  const loadWaterLogs = async () => {
    if (!authUser?.user_id) return;
    try {
      const response = await apiFetch(`/api/water/logs/${authUser.user_id}`);
      if (response.ok) {
        const data = await response.json();
        setWaterLogs(data.logs || data.water_logs || []);
      } else {
        const txt = await response.text();
        console.warn("Water logs not available:", txt);
        setWaterLogs([]);
      }
    } catch (error) {
      console.warn('Error loading water logs:', error);
      setWaterLogs([]);
    }
  };

  const addWater = async ({ ounces, date }) => {
    try {
      const response = await apiFetch(`/api/water/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: authUser.user_id,
          ounces: Number(ounces) || 0,
          date
        })
      });

      if (response.ok) {
        await loadWaterLogs();
        await loadDashboardData();
        showToast('Water logged successfully!', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error logging water: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error logging water: ${error?.message || String(error)}`, 'error');
    }
  };

  const deleteWaterLog = async (waterLogId) => {
    if (!window.confirm('Delete this water entry?')) return;
    try {
      const response = await apiFetch(`/api/water/log/${waterLogId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await loadWaterLogs();
        await loadDashboardData();
        showToast('Water entry deleted.', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error deleting water log: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error deleting water log: ${error?.message || String(error)}`, 'error');
    }
  };

  const loadExerciseLogs = useCallback(async () => {
    if (!authUser?.user_id) return;
    try {
      const response = await apiFetch(`/api/exercise/logs/${authUser.user_id}`);
      if (response.ok) {
        const data = await response.json();
        setExerciseLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error loading exercise logs:', error);
    }
  }, [apiFetch, authUser?.user_id]);

  const loadFoodFavorites = async () => {
    if (!authUser?.user_id) return;
    try {
      const response = await apiFetch(`/api/food/favorites/${authUser.user_id}`);
      if (response.ok) {
        const data = await response.json();
        setFoodFavorites(data.favorites || []);
      }
    } catch (error) {
      console.error('Error loading food favorites:', error);
    }
  };

  const loadExerciseFavorites = async () => {
    if (!authUser?.user_id) return;
    try {
      const response = await apiFetch(`/api/exercise/favorites/${authUser.user_id}`);
      if (response.ok) {
        const data = await response.json();
        setExerciseFavorites(data.favorites || []);
      }
    } catch (error) {
      console.error('Error loading exercise favorites:', error);
    }
  };

  const loadFavorites = async () => {
    if (favoritesType === 'exercise') return loadExerciseFavorites();
    return loadFoodFavorites();
  };

  const loadHistory = async () => {
    if (!authUser?.user_id) return;
    try {
      const response = await apiFetch(`/api/history/${authUser.user_id}?days=7`);
      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const loadPredefinedExercises = async () => {
    try {
      const response = await apiFetch(`/api/exercise/predefined`, { skipAuth: true });
      if (response.ok) {
        const data = await response.json();
        setPredefinedExercises(data.exercises || []);
      }
    } catch (error) {
      console.error('Error loading exercises:', error);
    }
  };

  const loadAdminUsers = async () => {
    try {
      const response = await apiFetch(`/api/admin/users`);
      if (response.ok) {
        const data = await response.json();
        setAdminUsers(data.users || []);
      } else {
        const error = await response.text();
        console.error('Error loading admin users:', error);
        showToast('Error loading users. You may need to logout and login again to refresh your admin session.', 'error');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showToast('Error loading users. Please try again.', 'error');
    }
  };

  const loadMessages = async () => {
    if (!authUser?.user_id) return;
    try {
      const response = await apiFetch(`/api/messages/${authUser.user_id}`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const loadGoals = async () => {
    if (!authUser?.user_id) return;
    try {
      const response = await apiFetch(`/api/goals/${authUser.user_id}`);
      if (response.ok) {
        const data = await response.json();
        setGoals(data.goals || []);
      }
    } catch (error) {
      console.error('Error loading goals:', error);
    }
  };

  const loadUnreadCount = async () => {
    if (!authUser?.user_id) return;
    try {
      const response = await apiFetch(`/api/messages/${authUser.user_id}/unread-count`);
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await apiFetch(`/api/admin/user/${userId}/details`);
      if (response.ok) {
        const data = await response.json();
        setSelectedUser(data);
      }
    } catch (error) {
      showToast('Error loading user details', 'error');
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const workoutsLove = JSON.parse(formData.get("workouts_love_json") || "[]");
    const preferredDays = JSON.parse(formData.get("preferred_workout_days_json") || "[]");
    const bloodworkFile = formData.get("bloodwork_file_json")
      ? JSON.parse(formData.get("bloodwork_file_json"))
      : null;

    const profileData = {
      user_id: authUser.user_id,

      name: formData.get("name"),
      age: parseInt(formData.get("age")),
      gender: formData.get("gender"),
      height_feet: parseInt(formData.get("height_feet")),
      height_inches: parseInt(formData.get("height_inches")),
      weight: parseFloat(formData.get("weight")),
      goal_weight: parseFloat(formData.get("goal_weight")),
      activity_level: formData.get("activity_level_label"),

      questionnaire: {
        steps_range: formData.get("steps_range"),
        current_exercise: formData.get("current_exercise"),
        gym_access: formData.get("gym_access"),
        gym_experience: formData.get("gym_experience"),

        workouts_love: workoutsLove,
        workouts_love_other: formData.get("workouts_love_other"),

        workouts_days_per_week: formData.get("workouts_days_per_week"),
        preferred_workout_days: preferredDays,

        health_conditions: formData.get("health_conditions"),
        current_injuries: formData.get("current_injuries"),
        past_injuries: formData.get("past_injuries"),
        pain_notes: formData.get("pain_notes"),
        active_hobbies: formData.get("active_hobbies"),

        eating_habits: formData.get("eating_habits"),
        foods_love: formData.get("foods_love"),
        foods_hate_allergies: formData.get("foods_hate_allergies"),
        fruits_veggies: formData.get("fruits_veggies"),
        supplements: formData.get("supplements"),
        yo_yo_diet: formData.get("yo_yo_diet"),

        tracked_macros_before: formData.get("tracked_macros_before"),
        smart_watch: formData.get("smart_watch"),

        bloodwork_file: bloodworkFile,
      },
    };

    try {
      const response = await apiFetch(`/api/users/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
        goToView('dashboard');
        showToast('Profile saved.', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error saving profile: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error saving profile: ${error.message}`, 'error');
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: 1280, height: 720 }
      });

      streamRef.current = stream;
      setShowCamera(true);

      setTimeout(() => {
        if (cameraVideoRef.current) cameraVideoRef.current.srcObject = stream;
      }, 0);
    } catch (error) {
      showToast(`Error accessing camera: ${error.message}`, 'error');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const analyzeExerciseMachine = async (imageBlob) => {
    const formData = new FormData();
    formData.append('file', imageBlob);

    const res = await apiFetch(`/api/exercise/analyze-machine`, {
      method: 'POST',
      body: formData
    });

    const data = await res.json();
    if (!data.success) {
      showToast(data.error || 'Could not analyze machine', 'error');
      return;
    }

    setOpenExerciseMachine((n) => n + 1);
  };

  const captureImage = async (videoRef, canvasRef) => {
    if (!videoRef.current || !canvasRef.current || analysisState.active) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(async (blob) => {
      startAnalysisUi(exerciseCameraMode ? "exercise-photo" : "food-photo", "analyzing");
      try {
        if (exerciseCameraMode) {
          await analyzeExerciseMachine(blob);
          setExerciseCameraMode(false);
        } else {
          await analyzeFood(blob);
        }
      } finally {
        stopAnalysisUi();
        stopCamera();
      }
    }, 'image/jpeg', 0.8);
  };

  const handleFileUpload = async (file, detailsFromUI) => {
    if (!file || analysisState.active) return;
    if (detailsFromUI && detailsFromUI.trim()) {
      setDetails(detailsFromUI);
    }
    startAnalysisUi("food-photo", "analyzing");
    try {
      await analyzeFood(file);
    } finally {
      stopAnalysisUi();
    }
  };

  const analyzeFood = async (imageBlob) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', imageBlob);

      if (details && details.trim()) {
        formData.append('details', details.trim());
      }

      const response = await apiFetch(`/api/food/analyze`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        const nutritionData = result.data;

        setAnalyzedFood({
          ...nutritionData,
          servings: 1,
          quantity: '1 serving',
          meal_type: 'lunch',
          notes: '',
          image_base64: result.image_base64,
          original: { ...nutritionData }
        });

        setShowFoodEditModal(true);
      } else {
        const err = await response.text();
        throw new Error(err || 'Analyze failed');
      }
    } catch (error) {
      showToast(`Error analyzing food: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFoodEditSubmit = async () => {
    if (!analyzedFood) return;

    await logFood({
      food_name: analyzedFood.food_name,
      quantity: analyzedFood.quantity || `${analyzedFood.servings || 1} serving${Number(analyzedFood.servings || 1) === 1 ? '' : 's'}`,
      calories: analyzedFood.calories,
      protein: analyzedFood.protein,
      carbs: analyzedFood.carbs,
      fats: analyzedFood.fats,
      meal_type: analyzedFood.meal_type,
      notes: analyzedFood.notes,
      image_base64: analyzedFood.image_base64
    });

    setShowFoodEditModal(false);
    setAnalyzedFood(null);
  };

  const updateAnalyzedFoodServings = (servings) => {
    if (!analyzedFood?.original) return;

    const s = parseFloat(servings) || 1;
    setAnalyzedFood(prev => ({
      ...prev,
      servings: s,
      calories: Math.round(prev.original.calories * s),
      protein: Math.round(prev.original.protein * s * 10) / 10,
      carbs: Math.round(prev.original.carbs * s * 10) / 10,
      fats: Math.round(prev.original.fats * s * 10) / 10
    }));
  };

    const getSpeechRecognitionCtor = () => {
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
  };

  const isNativeAndroid =
    !!window.Capacitor?.isNativePlatform?.() &&
    window.Capacitor?.getPlatform?.() === "android";

  const ensureNativeSpeechPermission = useCallback(async () => {
    if (!isNativeAndroid) return true;

    try {
      const current = await SpeechRecognition.checkPermissions();
      if (current?.speechRecognition === "granted" || current?.speechRecognition === true) {
        return true;
      }

      const requested = await SpeechRecognition.requestPermissions();
      return requested?.speechRecognition === "granted" || requested?.speechRecognition === true;
    } catch (e) {
      console.warn("Native speech permission check failed:", e);
      return false;
    }
  }, [isNativeAndroid]);

  const listenForSpeechWeb = useCallback((label = "voice log") => {
    return new Promise((resolve, reject) => {
      const SpeechRecognitionCtor = getSpeechRecognitionCtor();

      if (!SpeechRecognitionCtor) {
        reject(new Error("Voice recognition is not supported in this browser."));
        return;
      }

      const recognition = new SpeechRecognitionCtor();
      recognition.lang = "en-US";
      recognition.interimResults = true;
      recognition.continuous = false;
      recognition.maxAlternatives = 1;

      let settled = false;
      let transcript = "";
      let started = false;

      const finishResolve = (value) => {
        if (settled) return;
        settled = true;
        resolve(String(value || "").trim());
      };

      const finishReject = (message) => {
        if (settled) return;
        settled = true;
        reject(new Error(message));
      };

      recognition.onstart = () => {
        started = true;
      };

      recognition.onaudiostart = () => {
        started = true;
      };

      recognition.onresult = (event) => {
        let nextTranscript = "";
        let hasFinal = false;

        for (let i = 0; i < event.results.length; i += 1) {
          nextTranscript += event.results[i]?.[0]?.transcript || "";
          if (event.results[i]?.isFinal) hasFinal = true;
        }

        transcript = nextTranscript.trim();

        if (hasFinal && transcript) {
          finishResolve(transcript);
        }
      };

      recognition.onerror = (event) => {
        const code = event?.error || "unknown";

        if ((code === "aborted" || code === "no-speech") && transcript) {
          finishResolve(transcript);
          return;
        }

        if (code === "not-allowed" || code === "service-not-allowed") {
          finishReject("Microphone permission is required for voice logging.");
          return;
        }

        finishReject(`Could not capture ${label}: ${code}`);
      };

      recognition.onend = () => {
        if (settled) return;
        if (transcript) {
          finishResolve(transcript);
          return;
        }
        if (!started) {
          finishReject(`Voice capture did not start for ${label}.`);
          return;
        }
        finishReject(`No ${label} captured.`);
      };

      try {
        recognition.start();
      } catch (e) {
        finishReject(e?.message || `Could not start ${label}.`);
      }
    });
  }, []);

  const listenForSpeechNative = useCallback(async (label = "voice log") => {
    const allowed = await ensureNativeSpeechPermission();
    if (!allowed) {
      throw new Error("Microphone permission is required for voice logging.");
    }

    const availability = await SpeechRecognition.available().catch(() => ({ available: false }));
    if (!availability?.available) {
      throw new Error("Native speech recognition is not available on this device.");
    }

    try {
      await SpeechRecognition.removeAllListeners();
    } catch {}

    const result = await SpeechRecognition.start({
      language: "en-US",
      maxResults: 1,
      partialResults: false,
      popup: true,
      prompt: `Speak your ${label}`,
    });

    const transcript = Array.isArray(result?.matches)
      ? String(result.matches[0] || "").trim()
      : "";

    try {
      await SpeechRecognition.stop();
      await SpeechRecognition.removeAllListeners();
    } catch {}

    if (!transcript) {
      throw new Error(`No ${label} captured.`);
    }

    return transcript;
  }, [ensureNativeSpeechPermission]);

  const listenForSpeech = useCallback(async (label = "voice log") => {
    if (isNativeAndroid) {
      return listenForSpeechNative(label);
    }
    return listenForSpeechWeb(label);
  }, [isNativeAndroid, listenForSpeechNative, listenForSpeechWeb]);

  const parseDurationMinutesFromSpeech = (text) => {
    const input = String(text || "");

    const hoursMatch = input.match(/(\d+(?:\.\d+)?)\s*(?:hr|hrs|hour|hours)\b/i);
    if (hoursMatch) {
      return Math.max(1, Math.round(Number(hoursMatch[1]) * 60));
    }

    const minMatch = input.match(/(\d+(?:\.\d+)?)\s*(?:min|mins|minute|minutes)\b/i);
    if (minMatch) {
      return Math.max(1, Math.round(Number(minMatch[1])));
    }

    return 30;
  };

  const parseCaloriesFromSpeech = (text) => {
    const input = String(text || "");
    const match = input.match(/(\d+(?:\.\d+)?)\s*(?:cal|cals|calorie|calories)\b/i);
    return match ? Math.round(Number(match[1])) : null;
  };

  const cleanExerciseNameFromSpeech = (text) => {
    return String(text || "")
      .replace(/\bfor\b.*$/i, "")
      .replace(/\b\d+(?:\.\d+)?\s*(?:hr|hrs|hour|hours|min|mins|minute|minutes)\b/gi, "")
      .replace(/\b\d+(?:\.\d+)?\s*(?:cal|cals|calorie|calories)\b/gi, "")
      .replace(/\b(burned|burn|did|performed|completed|logged)\b/gi, "")
      .replace(/\s+/g, " ")
      .trim();
  };
  const logExercise = useCallback(async (exerciseData, formEl) => {
    try {
      const response = await apiFetch(`/api/exercise/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: authUser.user_id,
          ...exerciseData
        })
      });

      if (response.ok) {
        if (formEl?.reset) formEl.reset();
        loadExerciseLogs();
        loadDashboardData();
        showToast('Exercise logged successfully!', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error logging exercise: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error logging exercise: ${error.message}`, 'error');
    }
  }, [apiFetch, authUser?.user_id, showToast, loadDashboardData, loadExerciseLogs]);

  const handleVoiceFoodLog = useCallback(async () => {
    try {
      setLogChoice(null);
      setLogChoiceOrigin(null);
      startAnalysisUi("food-voice", "listening", "Listening for your food log…");
      showToast("Listening for food log…", "info");
      await new Promise((resolve) => window.setTimeout(resolve, 150));
      const transcript = await listenForSpeech("food log");

      if (!transcript) {
        showToast("No food detected from voice.", "warning");
        return;
      }

      startAnalysisUi("food-voice", "analyzing", "Analyzing your food log…");

      const form = new FormData();
      form.append("food_name", transcript);
      form.append("quantity", "1 serving");

      let estimated = {
        calories: 0,
        protein: 0,
        carbs: 0,
        fats: 0,
      };

      try {
        const response = await fetch(`${BACKEND_URL}/api/food/estimate-nutrition`, {
          method: "POST",
          body: form,
        });

        if (response.ok) {
          const data = await response.json();
          estimated = {
            calories: Number(data?.calories) || 0,
            protein: Number(data?.protein) || 0,
            carbs: Number(data?.carbs) || 0,
            fats: Number(data?.fats) || 0,
          };
        }
      } catch (e) {
        console.warn("Food voice estimate failed:", e);
      }

      setAnalyzedFood({
        food_name: transcript,
        calories: estimated.calories,
        protein: estimated.protein,
        carbs: estimated.carbs,
        fats: estimated.fats,
        servings: 1,
        quantity: '1 serving',
        meal_type: "lunch",
        notes: `Voice log: ${transcript}`,
        image_base64: null,
        original: { ...estimated },
      });

      setShowFoodEditModal(true);
      showToast(`Captured food: "${transcript}"`, "success");
    } catch (error) {
      showToast(`Voice food log error: ${error?.message || String(error)}`, "error");
    } finally {
      stopAnalysisUi();
    }
  }, [listenForSpeech, showToast]);

  const handleVoiceExerciseLog = useCallback(async () => {
    try {
      setLogChoice(null);
      setLogChoiceOrigin(null);
      startAnalysisUi("exercise-voice", "listening", "Listening for your exercise log…");
      showToast("Listening for exercise log…", "info");
      await new Promise((resolve) => window.setTimeout(resolve, 150));
      const transcript = await listenForSpeech("exercise log");

      if (!transcript) {
        showToast("No exercise detected from voice.", "warning");
        return;
      }

      startAnalysisUi("exercise-voice", "analyzing", "Analyzing your exercise log…");

      const duration = parseDurationMinutesFromSpeech(transcript);
      const explicitCalories = parseCaloriesFromSpeech(transcript);
      const cleanedName = cleanExerciseNameFromSpeech(transcript);

      if (!cleanedName) {
        showToast("Could not determine exercise name from voice.", "error");
        return;
      }

      const cleanedLower = cleanedName.toLowerCase();

      const matchedExercise =
        predefinedExercises.find((ex) => cleanedLower.includes(String(ex.name || "").toLowerCase())) ||
        predefinedExercises.find((ex) => String(ex.name || "").toLowerCase().includes(cleanedLower));

      const exerciseName = matchedExercise?.name || cleanedName;
      const calories =
        explicitCalories != null
          ? explicitCalories
          : Math.round((Number(matchedExercise?.calories_per_minute) || 0) * duration);

      await logExercise({
        exercise_name: exerciseName,
        duration_minutes: duration,
        calories_burned: calories,
        exercise_type: "voice",
        notes: `Voice log: ${transcript}`,
      });

      showToast(`Logged exercise: ${exerciseName}`, "success");
    } catch (error) {
      showToast(`Voice exercise log error: ${error?.message || String(error)}`, "error");
    } finally {
      stopAnalysisUi();
    }
  }, [listenForSpeech, predefinedExercises, showToast, logExercise]);

  const logFood = async (foodData) => {
    try {
      const response = await apiFetch(`/api/food/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: authUser.user_id,
          ...foodData
        })
      });

      if (response.ok) {
        loadFoodLogs();
        loadDashboardData();
        showToast('Food logged successfully!', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error logging food: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error logging food: ${error.message}`, 'error');
    }
  };

  const handleManualFoodLog = async (foodData) => {
    await logFood(foodData);
  };

  const deleteFoodLog = async (logId) => {
    if (!window.confirm('Delete this food log?')) return;

    try {
      const response = await apiFetch(`/api/food/log/${logId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadFoodLogs();
        loadDashboardData();
        showToast('Food log deleted.', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error deleting log: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error deleting log: ${error.message}`, 'error');
    }
  };

  const editFoodLog = async (editedLog) => {
    try {
      const response = await apiFetch(`/api/food/log/${editedLog.log_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: authUser.user_id,
          food_name: editedLog.food_name,
          quantity: editedLog.quantity || '',
          calories: editedLog.calories,
          protein: editedLog.protein,
          carbs: editedLog.carbs,
          fats: editedLog.fats,
          fiber: editedLog.fiber ?? editedLog.fiber_g ?? 0,
          meal_type: editedLog.meal_type
        })
      });

      if (response.ok) {
        loadFoodLogs();
        loadDashboardData();
        showToast('Food updated.', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error updating food: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error updating log: ${error.message}`, 'error');
    }
  };

  

  const deleteExerciseLog = async (logId) => {
    if (!window.confirm('Delete this exercise log?')) return;

    try {
      const response = await apiFetch(`/api/exercise/log/${logId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadExerciseLogs();
        loadDashboardData();
        showToast('Exercise log deleted.', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error deleting log: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error deleting log: ${error.message}`, 'error');
    }
  };

  const addToFavorites = async (foodItem) => {
    try {
      const response = await apiFetch(`/api/food/favorites/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: authUser.user_id,
          food_name: foodItem.food_name,
          calories: foodItem.calories,
          protein: foodItem.protein,
          carbs: foodItem.carbs,
          fats: foodItem.fats
        })
      });

      if (response.ok) {
        showToast('Added to favorites!', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error adding to favorites: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error adding to favorites: ${error.message}`, 'error');
    }
  };

  const calculateExerciseCalories = (exerciseName, duration) => {
    const exercise = predefinedExercises.find(ex => ex.name === exerciseName);
    if (exercise) {
      return exercise.calories_per_minute * duration;
    }
    return 0;
  };

  const handleNavClick = (view) => {
    goToView(view);
    setMenuOpen(false);
    setOpenSubNav(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigate = (view, opts = {}) => {
    goToView(view, opts);
    setMenuOpen(false);
    setOpenSubNav(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openLogChoice = (kind, originView) => {
    setLogChoiceOrigin(originView || currentView || null);
    setLogChoice(kind);
  };



  const cancelLogChoice = () => {
    const origin = logChoiceOrigin;
    setLogChoice(null);
    setLogChoiceOrigin(null);

    if (origin === "dashboard") {
      goToView("dashboard");
    }
  };

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    setOpenSubNav(null);
  };

  const handleEditProfile = () => {
    setProfile(profile);
    goToView('dashboard');
    setOpenSubNav(null);
    setEditingProfile(true);
  };

  const sendMessage = async (messageData) => {
    try {
      const response = await apiFetch(`/api/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(messageData)
      });

      if (response.ok) {
        loadMessages();
        showToast('Message sent successfully!', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error sending message: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error sending message: ${error.message}`, 'error');
    }
  };

  const markMessageRead = async (messageId) => {
    setMessages(prev =>
      prev.map(m => {
        const id = m.message_id ?? m.id ?? m._id;
        return id === messageId ? { ...m, is_read: true, read: true, read_at: m.read_at ?? new Date().toISOString() } : m;
      })
    );

    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      const res = await apiFetch(`/api/messages/${messageId}/read`, {
        method: 'PUT'
      });

      if (!res.ok) {
        setMessages(prev =>
          prev.map(m => {
            const id = m.message_id ?? m.id ?? m._id;
            return id === messageId ? { ...m, is_read: false, read: false, read_at: null } : m;
          })
        );
        setUnreadCount(prev => prev + 1);
        const errText = await res.text();
        throw new Error(errText || 'Failed to mark read');
      }

      loadUnreadCount();
      loadMessages();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const createGoal = async (goalData) => {
    try {
      const response = await apiFetch(`/api/goals/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(goalData)
      });

      if (response.ok) {
        loadGoals();
        if (authUser.is_admin) loadAdminUsers();
        showToast('Goal created successfully!', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error creating goal: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error creating goal: ${error.message}`, 'error');
    }
  };

  const completeGoal = async (goalId) => {
    try {
      const response = await apiFetch(`/api/goals/${goalId}/complete`, {
        method: 'PUT'
      });

      if (response.ok) {
        loadGoals();
        showToast('Goal marked as complete!', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error completing goal: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error completing goal: ${error.message}`, 'error');
    }
  };

  const deleteGoal = async (goalId) => {
    if (!window.confirm('Delete this goal?')) return;

    try {
      const response = await apiFetch(`/api/goals/${goalId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadGoals();
        showToast('Goal deleted.', 'success');
      } else {
        const err = await safeJson(response);
        showToast(`Error deleting goal: ${err?.detail || err || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error deleting goal: ${error.message}`, 'error');
    }
  };

  if (!profile || editingProfile) {
    return (
      <ProfileSetup
        authUser={authUser}
        profile={profile}
        onSave={async (e) => {
          await saveProfile(e);
          setEditingProfile(false);
        }}
        onLogout={logout}
        showToast={showToast}
      />
    );
  }

  const zoomPercent = Math.round((Number(appZoom) || 1) * 100);

  return (
    <div className="app">
      <nav className="nav-header">
        <div className="nav-brand">
          <h2 className="heading-4">HealthTrail</h2>
          <span className="body-small" style={{ marginLeft: "12px", color: "var(--text-muted)" }}>
            Hi, {authUser.name}!
          </span>
        </div>

        <div className="nav-zoom-controls" aria-label="Display zoom controls">
          <button
            type="button"
            className="nav-zoom-btn"
            onClick={() => setAppZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))}
            aria-label="Zoom out"
            title="Zoom out"
          >
            −
          </button>
          <button
            type="button"
            className="nav-zoom-readout"
            onClick={() => setAppZoom(1)}
            aria-label="Reset zoom"
            title="Reset zoom"
          >
            {zoomPercent}%
          </button>
          <button
            type="button"
            className="nav-zoom-btn"
            onClick={() => setAppZoom((z) => Math.min(1.2, Math.round((z + 0.1) * 10) / 10))}
            aria-label="Zoom in"
            title="Zoom in"
          >
            +
          </button>
        </div>

        <button
          type="button"
          className="mobile-profile-btn"
          aria-label="Profile"
          onClick={() => handleNavClick("profile")}
          title="Profile"
        >
          👤
        </button>

        {/* DESKTOP NAV */}
        <div className="nav-links desktop-only">
          <button
            onClick={() => handleNavClick("dashboard")}
            className={currentView === "dashboard" ? "nav-link nav-link-main active" : "nav-link nav-link-main"}
          >
            🏠 Home
          </button>

          <button
            onClick={() => handleNavClick("calendar")}
            className={currentView === "calendar" ? "nav-link nav-link-main active" : "nav-link nav-link-main"}
          >
            📅 Calendar
          </button>

          {/* Workouts */}
          <div
            className={`nav-dropdown ${openSubNav === "workouts" ? "open" : ""}`}
            style={{ position: "relative" }}
            onMouseLeave={() => setOpenSubNav(null)}
          >
            <button
              type="button"
              className={
                currentView === "exercise" ||
                (currentView === "favorites" && favoritesType === "exercise") ||
                (currentView === "history" && historyType === "exercise")
                  ? "nav-link nav-link-main active nav-dropdown-trigger"
                  : "nav-link nav-link-main nav-dropdown-trigger"
              }
              style={{ display: "flex", alignItems: "center", gap: 6 }}
              onClick={() => setOpenSubNav((prev) => (prev === "workouts" ? null : "workouts"))}
            >
              🏋️ Workouts <span className="nav-caret">▾</span>
            </button>

            <div className="nav-dropdown-menu">
              <button
                onClick={() => handleNavClick("exercise")}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                Fitness Tracking
              </button>
              <button
                onClick={() => handleNavClick("workouts")}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                Your Program
              </button>

              <button
                onClick={() => navigate("favorites", { favoritesType: "exercise" })}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                Favorites
              </button>

              <button
                onClick={() => navigate("history", { historyType: "exercise" })}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                History
              </button>
            </div>
          </div>

          {/* Meals */}
          <div
            className={`nav-dropdown ${openSubNav === "meals" ? "open" : ""}`}
            style={{ position: "relative" }}
            onMouseLeave={() => setOpenSubNav(null)}
          >
            <button
              type="button"
              className={
                currentView === "food" ||
                (currentView === "favorites" && favoritesType === "food") ||
                (currentView === "history" && historyType === "food")
                  ? "nav-link nav-link-main active nav-dropdown-trigger"
                  : "nav-link nav-link-main nav-dropdown-trigger"
              }
              style={{ display: "flex", alignItems: "center", gap: 6 }}
              onClick={() => setOpenSubNav((prev) => (prev === "meals" ? null : "meals"))}
            >
              🍽️ Meals <span className="nav-caret">▾</span>
            </button>

            <div className="nav-dropdown-menu">
              <button
                onClick={() => handleNavClick("food")}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                Nutrition Tracking
              </button>

              <button
                onClick={() => handleNavClick("nutrition")}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                Meal Program
              </button>

              <button
                onClick={() => navigate("favorites", { favoritesType: "food" })}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                Log Favorites
              </button>

              <button
                onClick={() => navigate("history", { historyType: "food" })}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                History
              </button>
            </div>
          </div>

          {/* Goals dropdown */}
          <div
            className={`nav-dropdown ${openSubNav === "goals" ? "open" : ""}`}
            style={{ position: "relative" }}
            onMouseLeave={() => setOpenSubNav(null)}
          >
            <button
              type="button"
              className={
                ["goals", "habits", "coach", "coach_programs"].includes(currentView)
                  ? "nav-link nav-link-main active nav-dropdown-trigger"
                  : "nav-link nav-link-main nav-dropdown-trigger"
              }
              style={{ display: "flex", alignItems: "center", gap: 6 }}
              onClick={() => setOpenSubNav((prev) => (prev === "goals" ? null : "goals"))}
            >
              ⚕️ Health Trail <span className="nav-caret">▾</span>
            </button>

            <div className="nav-dropdown-menu">
              <button
                type="button"
                className="nav-link nav-dropdown-item"
                onClick={() => handleNavClick("coach_programs")}
                data-testid="nav-coach-programs"
              >
                AI Total Program
              </button>
              <button
                onClick={() => handleNavClick("goals")}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                Goals
              </button>
              

              <button
                onClick={() => handleNavClick("habits")}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                Habits
              </button>

              <button
                onClick={() => handleNavClick("coach")}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                Ask Coach
              </button>
            </div>
          </div>

          {/* Settings */}
          <div
            className={`nav-dropdown ${openSubNav === "settings" ? "open" : ""}`}
            style={{ position: "relative" }}
            onMouseLeave={() => setOpenSubNav(null)}
          >
            <button
              type="button"
              className={
                ["profile", "admin", "trainer", "devices", "messages", "weight"].includes(currentView) ||
                (currentView === "history" && historyType === "weight")
                  ? "nav-link nav-link-main active nav-dropdown-trigger"
                  : "nav-link nav-link-main nav-dropdown-trigger"
              }
              style={{ display: "flex", alignItems: "center", gap: 6 }}
              onClick={() => setOpenSubNav((prev) => (prev === "settings" ? null : "settings"))}
            >
              ⚙️ Settings <span className="nav-caret">▾</span>
            </button>

            <div className="nav-dropdown-menu">
              <button
                onClick={() => handleNavClick("profile")}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                Profile
              </button>

              <button
                onClick={() => handleNavClick("devices")}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                Connect Devices
              </button>

              <button
                onClick={() => handleNavClick("messages")}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left", position: "relative" }}
              >
                Messages {unreadCount > 0 && <span className="message-badge">{unreadCount}</span>}
              </button>

              <button
                onClick={() => handleNavClick("weight")}
                className="nav-link nav-dropdown-item"
                style={{ width: "100%", textAlign: "left" }}
              >
                Health Tracking
              </button>

              {authUser?.is_admin && (
                <button
                  onClick={() => handleNavClick("admin")}
                  className="nav-link nav-dropdown-item"
                  style={{ width: "100%", textAlign: "left" }}
                >
                  Admin Dashboard
                </button>
              )}

              {authUser?.is_trainer && (
                <button
                  onClick={() => handleNavClick("trainer")}
                  className="nav-link nav-dropdown-item"
                  style={{ width: "100%", textAlign: "left" }}
                >
                  Trainer Dashboard
                </button>
              )}

              <button
                onClick={handleLogout}
                className="nav-link nav-dropdown-item logout-link"
                style={{ width: "100%", textAlign: "left" }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>

      </nav>

      {/* MOBILE HEADER TABS */}
      <div className="mobile-header-tabs-shell">
        <HeaderTabs
          currentView={currentView}
          favoritesType={favoritesType}
          historyType={historyType}
          authUser={authUser}
          onNavigate={(view, opts = {}) => {
            if (view === "favorites") {
              navigate("favorites", { favoritesType: opts.favoritesType || opts._favoritesType || favoritesType || "food" });
              return;
            }
            if (view === "history") {
              navigate("history", { historyType: opts.historyType || opts._historyType || historyType || "overview" });
              return;
            }
            handleNavClick(view);
          }}
        />
      </div>

      {menuOpen && <div className="nav-overlay" onClick={() => { setMenuOpen(false); setOpenSubNav(null); }}></div>}

      <WeighInModal
        isOpen={showWeighInModal}
        onClose={() => setShowWeighInModal(false)}
        onSubmit={handleWeighInSubmit}
        userId={authUser?.user_id}
        token={token}
        currentWeight={profile?.weight}
        showToast={showToast}
      />

      <GoalModal
        open={goalModalOpen}
        onClose={closeGoalModal}
        token={token}
        currentUserId={authUser?.user_id}
        canManageOthers={canManageOthers}
        people={people}
        onCreated={(newGoal) => {
          loadGoals();

          if (currentView === "dashboard") {
            loadDashboardData();
          }

          if (currentView === "calendar") {
            loadGoals();
          }

          if (goalModalSource === "coach_programs") {
            window.dispatchEvent(
              new CustomEvent("coach_programs:return_goal_created", { detail: newGoal })
            );
          }

          window.dispatchEvent(new CustomEvent("goal:created", { detail: newGoal }));
        }}
        showToast={showToast}
      />

      {/* Global Camera Modal */}
      {showCamera && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: '20px'
          }}
          onMouseDown={stopCamera}
        >
          <div
            className="modal-content"
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              padding: '16px',
              width: 'min(720px, 100%)',
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="heading-4">
                {exerciseCameraMode ? '📷 Capture Machine Readout' : '📸 Capture Food Photo'}
              </div>
              <button className="btn-secondary" type="button" onClick={stopCamera}>Close</button>
            </div>

            {analysisState.active ? (
              <div className="camera-analyzing-view">
                <div className="camera-analyzing-graphic" aria-hidden="true">
                  <div className="camera-analyzing-ring camera-analyzing-ring--one"></div>
                  <div className="camera-analyzing-ring camera-analyzing-ring--two"></div>
                  <div className="camera-analyzing-core">AI</div>
                </div>
                <div className="heading-4" style={{ marginBottom: 8 }}>{getAnalysisTitle()}</div>
                <div className="body-small" style={{ color: 'var(--text-muted)', textAlign: 'center' }}>
                  {analysisState.message || 'Please wait while we process your input.'}
                </div>
              </div>
            ) : (
              <video ref={cameraVideoRef} autoPlay playsInline style={{ width: '100%', borderRadius: 12 }} />
            )}
            <canvas ref={cameraCanvasRef} style={{ display: 'none' }} />

            <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              <button
                className="btn-primary"
                type="button"
                style={{ flex: 1 }}
                onClick={() => captureImage(cameraVideoRef, cameraCanvasRef)}
                disabled={analysisState.active}
              >
                {analysisState.active ? 'Analyzing…' : 'Capture'}
              </button>
              <button
                className="btn-secondary"
                type="button"
                style={{ flex: 1 }}
                onClick={stopCamera}
                disabled={analysisState.active}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      

      {/* Food Edit Modal */}
      {showFoodEditModal && analyzedFood && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div className="modal-content" style={{
            background: 'var(--bg-primary)',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            overflowY: 'auto'
          }}>
            <h2 className="heading-3" style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>📸</span> Food Detected
            </h2>

            {analyzedFood.image_base64 && (
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <img
                  src={`data:image/jpeg;base64,${analyzedFood.image_base64}`}
                  alt="Food"
                  style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover' }}
                />
              </div>
            )}

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="body-medium">Food Name</label>
              <input
                type="text"
                value={analyzedFood.food_name}
                onChange={(e) => setAnalyzedFood(prev => ({ ...prev, food_name: e.target.value }))}
                className="form-input"
                style={{ fontSize: '16px' }}
              />
            </div>

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div className="form-group">
                <label className="body-medium">Serving Multiplier</label>
                <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "nowrap", width: "100%" }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={analyzedFood.servings}
                    onChange={(e) => updateAnalyzedFoodServings(e.target.value)}
                    className="form-input"
                    style={{
                      fontSize: "16px",
                      width: "80px",
                      textAlign: "center",
                      flex: "0 0 80px",
                      minWidth: "80px",
                    }}
                  />

                  <div style={{ display: "flex", gap: "6px", flexWrap: "nowrap", flex: "0 0 auto", whiteSpace: "nowrap" }}>
                    {[0.5, 1, 2].map((s) => {
                      const isSelected = Number(analyzedFood.servings) === s;

                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => updateAnalyzedFoodServings(s)}
                          style={{
                            padding: "9px 12px",
                            borderRadius: "10px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: 700,
                            lineHeight: 1,
                            minWidth: "48px",
                            flex: "0 0 auto",
                            background: isSelected ? "var(--gradient-button)" : "rgba(255,255,255,0.6)",
                            color: isSelected ? "#fff" : "var(--text-primary)",
                            border: isSelected ? "1px solid rgba(0,0,0,0.25)" : "1px solid var(--border-light)",
                            boxShadow: isSelected ? "0 6px 16px rgba(0,0,0,0.18)" : "none",
                            transform: isSelected ? "translateY(-1px)" : "none",
                            transition: "all 0.15s ease",
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.background = "rgba(24,24,24,0.05)";
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.6)";
                          }}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="body-medium">Meal Type</label>
                <select
                  value={analyzedFood.meal_type}
                  onChange={(e) => setAnalyzedFood(prev => ({ ...prev, meal_type: e.target.value }))}
                  className="form-input"
                  style={{ fontSize: '16px' }}
                >
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="dinner">Dinner</option>
                  <option value="snack">Snack</option>
                  <option value="beverage">Beverage</option>
                </select>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="body-medium">Serving Size</label>
              <input
                type="text"
                value={analyzedFood.quantity || ''}
                onChange={(e) => setAnalyzedFood(prev => ({ ...prev, quantity: e.target.value }))}
                className="form-input"
                placeholder="e.g. 1 bowl, 200g, 2 slices"
                style={{ fontSize: '16px' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="body-medium">Notes (optional)</label>
              <textarea
                value={analyzedFood.notes || ''}
                onChange={(e) => setAnalyzedFood(prev => ({ ...prev, notes: e.target.value }))}
                className="form-input"
                placeholder="Add any notes about this food..."
                rows="2"
                style={{ fontSize: '16px', resize: 'vertical' }}
              />
            </div>

            <div style={{
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ marginBottom: '8px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                Nutrition (for {analyzedFood.servings} serving{analyzedFood.servings !== 1 ? 's' : ''})
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: 'var(--text-muted)' }}>Calories</label>
                  <input
                    type="string"
                    value={analyzedFood.calories}
                    onChange={(e) => setAnalyzedFood(prev => ({ ...prev, calories: parseInt(e.target.value) || 0 }))}
                    className="form-input"
                    style={{ fontSize: '16px', fontWeight: '600' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: 'var(--text-muted)' }}>Protein (g)</label>
                  <input
                    type="string"
                    step="0.1"
                    value={analyzedFood.protein}
                    onChange={(e) => setAnalyzedFood(prev => ({ ...prev, protein: parseFloat(e.target.value) || 0 }))}
                    className="form-input"
                    style={{ fontSize: '16px' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: 'var(--text-muted)' }}>Carbs (g)</label>
                  <input
                    type="string"
                    step="0.1"
                    value={analyzedFood.carbs}
                    onChange={(e) => setAnalyzedFood(prev => ({ ...prev, carbs: parseFloat(e.target.value) || 0 }))}
                    className="form-input"
                    style={{ fontSize: '16px' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="body-small" style={{ color: 'var(--text-muted)' }}>Fats (g)</label>
                  <input
                    type="string"
                    step="0.1"
                    value={analyzedFood.fats}
                    onChange={(e) => setAnalyzedFood(prev => ({ ...prev, fats: parseFloat(e.target.value) || 0 }))}
                    className="form-input"
                    style={{ fontSize: '16px' }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowFoodEditModal(false);
                  setAnalyzedFood(null);
                }}
                className="btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={handleFoodEditSubmit}
                className="btn-primary"
                style={{ flex: 1 }}
              >
                Log Nutrition
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container">
        {currentView === 'workout_session' && (
          <WorkoutSessionView
            token={token}
            sessionId={workoutSessionId}
            onBack={() => {
              const rv = workoutReturnView || "dashboard";
              setWorkoutSessionId(null);
              goToView(rv);
              setTimeout(() => {
                try { loadDashboardData(); } catch {}
                try { loadExerciseLogs(); } catch {}
              }, 0);
            }}
            showToast={showToast}
          />
        )}

        {currentView === 'dashboard' && (
          <Dashboard
            dashboardData={dashboardData}
            onNavigate={goToView}
            userId={authUser?.user_id}
            token={token}
            onOpenLogChoice={(kind) => openLogChoice(kind, "dashboard")}
            onStartWorkout={(eventId) => startWorkoutSession(eventId, "dashboard")}
            showToast={showToast}
          />
        )}

        {currentView === 'nutrition' && (
          <NutritionHub
            token={token}
            onBackToLogging={() => goToView("food")}
            showToast={showToast}
          />
        )}

        {currentView === 'food' && (
          <FoodTracker
            onNavigate={goToView}
            foodLogs={foodLogs}
            onStartCamera={startCamera}
            onFileUpload={handleFileUpload}
            onDeleteLog={deleteFoodLog}
            onAddToFavorites={addToFavorites}
            onManualLog={handleManualFoodLog}
            onEditLog={editFoodLog}
            onOpenLogChoice={() => openLogChoice("food", "food")}
            fileInputRef={foodFileInputRef}
            openManualTrigger={openFoodManual}
            openQuickAddTrigger={openFoodQuickAdd}
            waterLogs={waterLogs}
            onAddWater={addWater}
            onDeleteWaterLog={deleteWaterLog}
            token={token}
            showToast={showToast}
            mealFeedback={mealFeedback}
          />
        )}

        {currentView === 'trainer' && (
          <TrainerDashboard token={token} authUser={authUser} showToast={showToast} />
        )}

        {currentView === 'coach' && (
          <CoachView
            token={token}
            userName={authUser?.name}
            onOpenGoalModal={openGoalModal}
            currentView={currentView}
            showToast={showToast}
          />
        )}

        {currentView === 'coach_programs' && (
          <CoachView
            token={token}
            userName={profile?.name || profile?.username || authUser?.name}
            onOpenGoalModal={openGoalModal}
            currentView={currentView}
            mode="programs"
            showToast={showToast}
          />
        )}

        {currentView === 'workouts' && (
          <WorkoutsHub
            token={token}
            dashboardData={dashboardData}
            onBackToLogging={() => goToView("exercise")}
            onStartWorkout={(eventId) => startWorkoutSession(eventId, "workouts")}
            showToast={showToast}
          />
        )}

        {currentView === 'exercise' && (
          <ExerciseTracker
            onNavigate={goToView}
            onStartWorkout={(eventId) => startWorkoutSession(eventId, "exercise")}
            exerciseLogs={exerciseLogs}
            predefinedExercises={predefinedExercises}
            onLogExercise={logExercise}
            onDeleteLog={deleteExerciseLog}
            onCalculateCalories={calculateExerciseCalories}
            token={token}
            onOpenLogChoice={() => openLogChoice("exercise", "exercise")}
            fileInputRef={exerciseFileInputRef}
            openManualTrigger={openExerciseManual}
            openQuickAddTrigger={openExercisePredefined}
            openPredefinedTrigger={openExercisePredefined}
            openMachineTrigger={openExerciseMachine}
            userId={authUser.user_id}
            showToast={showToast}
          />
        )}

        {currentView === 'admin' && authUser?.is_admin && (
          <AdminDashboard
            adminUsers={adminUsers}
            selectedUser={selectedUser}
            onViewDetails={viewUserDetails}
            onBack={() => setSelectedUser(null)}
            token={token}
            adminUserId={authUser.user_id}
            onSendMessage={sendMessage}
            onCreateGoal={createGoal}
            onRefreshUsers={loadAdminUsers}
            showToast={showToast}
          />
        )}

        {currentView === 'devices' && (
          <WearablesView
            token={token}
            userId={authUser.user_id}
            showToast={showToast}
          />
        )}

        {currentView === 'messages' && (
          <MessagesView
            messages={messages}
            userId={authUser.user_id}
            isAdmin={authUser?.is_admin}
            onSendMessage={sendMessage}
            onMarkRead={markMessageRead}
            token={token}
            onRefreshMessages={loadUnreadCount}
            showToast={showToast}
          />
        )}

        {currentView === 'goals' && (
          <GoalsView
            goals={goals}
            userId={authUser.user_id}
            isAdmin={authUser?.is_admin}
            onCreateGoal={createGoal}
            onCompleteGoal={completeGoal}
            onDeleteGoal={deleteGoal}
            token={token}
            onOpenGoalModal={openGoalModal}
            currentView={currentView}
            showToast={showToast}
          />
        )}

        {currentView === 'favorites' && (
          <FavoritesView
            favorites={favoritesType === 'exercise' ? exerciseFavorites : foodFavorites}
            userId={authUser.user_id}
            type={favoritesType}
            onLogged={() => {
              if (favoritesType === 'exercise') {
                loadExerciseFavorites();
                loadExerciseLogs();
                loadDashboardData();
              } else {
                loadFoodFavorites();
                loadFoodLogs();
                loadDashboardData();
                loadWaterLogs();
              }
            }}
            onDeleted={() => {
              if (favoritesType === 'exercise') loadExerciseFavorites();
              else loadFoodFavorites();
            }}
            showToast={showToast}
          />
        )}

        {currentView === 'history' && (
          historyType === 'weight' ? (
            <WeightHistoryView userId={authUser?.user_id} token={token} />
          ) : (
            <HistoryView userId={authUser.user_id} token={token} type={historyType} />
          )
        )}

        {currentView === 'profile' && (
          <ProfileView profile={profile} onEditProfile={handleEditProfile} token={token} authUser={authUser} showToast={showToast} />
        )}

        {currentView === 'weight' && (
          <WeightHistoryView userId={authUser?.user_id} token={token} />
        )}

        {currentView === 'privacy' && <PrivacyPolicy />}
        {currentView === 'terms' && <TermsOfService />}

        {currentView === 'calendar' && (
          <CalendarView
            token={token}
            authUser={authUser}
            adminUsers={adminUsers}
            onStartWorkout={(eventId) => startWorkoutSession(eventId, "calendar")}
            showToast={showToast}
          />
        )}

        {currentView === 'habits' && (
          <HabitsView token={token} authUser={authUser} adminUsers={adminUsers} showToast={showToast} />
        )}
      </div>

      <MobileBottomNav
        currentView={currentView}
        onNavigate={(view) => {
          goToView(view);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        historyType={historyType}
        favoritesType={favoritesType}
      />
            <LogChoiceModal
        isOpen={Boolean(logChoiceConfig)}
        title={logChoiceConfig?.title}
        subtitle={logChoiceConfig?.subtitle}
        takePhotoLabel={logChoiceConfig?.takePhotoLabel}
        uploadLabel={logChoiceConfig?.uploadLabel}
        voiceLabel={logChoiceConfig?.voiceLabel}
        quickAddLabel={logChoiceConfig?.quickAddLabel}
        manualLabel={logChoiceConfig?.manualLabel}
        onClose={cancelLogChoice}
        onTakePhoto={() => {
          if (logChoice === "food") {
            setLogChoice(null);
            setExerciseCameraMode(false);
            startCamera();
            return;
          }

          if (logChoice === "exercise") {
            setLogChoice(null);
            setExerciseCameraMode(true);
            startCamera();
          }
        }}
        onUpload={() => {
          if (logChoice === "food") {
            setLogChoice(null);
            foodFileInputRef.current?.click();
            return;
          }

          if (logChoice === "exercise") {
            setLogChoice(null);
            exerciseFileInputRef.current?.click();
          }
        }}
        onVoice={async () => {
          const type = logChoice;
          setLogChoice(null);

          if (type === "food") {
            await handleVoiceFoodLog();
            return;
          }

          if (type === "exercise") {
            await handleVoiceExerciseLog();
          }
        }}
        onQuickAdd={() => {
          if (logChoice === "food") {
            setLogChoice(null);
            setOpenFoodQuickAdd((n) => n + 1);
            return;
          }

          if (logChoice === "exercise") {
            setLogChoice(null);
            setOpenExercisePredefined((n) => n + 1);
            return;
          }
        }}
        onManual={() => {
          if (logChoice === "food") {
            setLogChoice(null);
            setOpenFoodManual((n) => n + 1);
            return;
          }

          if (logChoice === "exercise") {
            setLogChoice(null);
            setOpenExerciseManual((n) => n + 1);
          }
        }}
      />

      {/* Footer Links */}
      <footer style={{
        padding: '20px',
        textAlign: 'center',
        background: 'var(--bg-secondary)',
        marginTop: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap' }}>
          <button
            onClick={() => goToView('privacy')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              textDecoration: 'underline'
            }}
            data-testid="privacy-link"
          >
            Privacy Policy
          </button>
          <button
            onClick={() => goToView('terms')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              textDecoration: 'underline'
            }}
            data-testid="terms-link"
          >
            Terms of Service
          </button>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '12px' }}>
          © 2025 HealthTrail. All rights reserved.
        </p>
      </footer>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
                  </div>
      )}
      {toast.open && (
  <div className={`app-toast app-toast--${toast.type}`} onClick={hideToast}>
    <div className="app-toast__content">
      <span>{toast.message}</span>
      <button type="button" className="app-toast__close" onClick={hideToast}>
        ✕
      </button>
    </div>
        </div>
      )}
    </div>
  );
}

export default App;
